/** @format */
import { MonoTypeOperatorFunction, Observable, Subject, Subscription } from 'rxjs';
import { concatMap, exhaustMap, finalize, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { Mono } from '../mono/monoUtils';

/**
 * Defines how a {@link RequestQueue} handles a request that is invoked while previous requests are still in progress:
 * - `ignoreConcurrent`: the new request is ignored
 * - `concurrent`: the new request is executed immediately, concurrently with the previous ones
 * - `sequential`: the new request is queued and executed only after all the previous requests have completed
 */
export type RequestQueueStrategy = 'ignoreConcurrent' | 'concurrent' | 'sequential';

/**
 * RequestQueue is a wrapper of a {@link Subject} (queue$) and its {@link Subscription}, which together
 * are configured to execute defined requests in a specific manner.
 */
export class RequestQueue<P, R> {
    readonly strategy: RequestQueueStrategy;
    private readonly queue$: Subject<P>;
    private readonly subscription: Subscription;
    inProgress: boolean = false;
    private pendingCount = 0;

    /**
     * Constructor for RequestQueue. Helper functions, e.g. {@link requestQueueIgnoreConcurrent}, should be preferred
     * over calling this constructor directly.
     *
     * @param strategy Defines how requests invoked while previous ones are still in progress are handled.
     * @param request Mono returning request function that is executed every time this RequestQueue's next is called.
     * @param resultHandler Function that is passed to the RequestQueue's subscribe call.
     * @param errorHandler MonoTypeOperatorFunction to where errors are piped to.
     * @param destroy$ Either an Observable which closes the queue upon first value emission, or an operator function which
     * can close the queue based on any condition/event (e.g. `takeUntilDestroyed()`).
     */
    constructor(
        strategy: RequestQueueStrategy,
        request: (p?: P) => Mono<R>,
        resultHandler: (r: R) => void,
        errorHandler: MonoTypeOperatorFunction<R>,
        destroy$?: Observable<unknown> | MonoTypeOperatorFunction<unknown>,
    ) {
        this.strategy = strategy;
        this.queue$ = new Subject<P>();
        const pipedQueue$ =
            strategy === 'sequential' ? this.sequentialQueue(request, errorHandler) : this.parallelQueue(request, errorHandler);
        if (destroy$) {
            const operator = typeof destroy$ === 'function' ? destroy$ : takeUntil(destroy$);
            this.subscription = pipedQueue$.pipe(operator).subscribe(resultHandler);
        } else {
            this.subscription = pipedQueue$.subscribe(resultHandler);
        }
    }

    /**
     * Whether requests invoked while a previous request is still in progress are ignored.
     */
    get ignoreConcurrent(): boolean {
        return this.strategy === 'ignoreConcurrent';
    }

    /**
     * Invoke the next request.
     *
     * @param value Argument to be passed to the request in case the related type is other than <code>void</code>
     */
    next(value: P): void {
        this.queue$.next(value);
    }

    /**
     * Alternative way to unsubscribe from the internal subscription in case <code>destroy$</code> was not provided
     * in the constructor.
     */
    unsubscribe(): void {
        this.subscription.unsubscribe();
    }

    /**
     * The closed state of the internal subscription, mainly for testing purposes.
     */
    get closed(): boolean {
        return this.subscription.closed;
    }

    private parallelQueue(request: (p?: P) => Mono<R>, errorHandler: MonoTypeOperatorFunction<R>): Observable<R> {
        // the following are not actually deprecated -> ignore ESLint and Sonar warnings
        // eslint-disable-next-line deprecation/deprecation
        const mapOperator = this.ignoreConcurrent ? exhaustMap : mergeMap; // NOSONAR
        return this.queue$.pipe(
            tap(() => (this.inProgress = true)),
            mapOperator(p =>
                request(p).pipe(
                    errorHandler,
                    finalize(() => (this.inProgress = false)),
                ),
            ),
        );
    }

    private sequentialQueue(request: (p?: P) => Mono<R>, errorHandler: MonoTypeOperatorFunction<R>): Observable<R> {
        // Count the queued requests too, so that inProgress stays true until the whole queue has been processed
        return this.queue$.pipe(
            tap(() => {
                this.pendingCount = this.pendingCount + 1;
                this.inProgress = true;
            }),
            concatMap(p =>
                request(p).pipe(
                    errorHandler,
                    finalize(() => {
                        this.pendingCount = this.pendingCount - 1;
                        this.inProgress = this.pendingCount > 0;
                    }),
                ),
            ),
        );
    }
}

/**
 * Helper function for constructing a {@link RequestQueue} instance.
 *
 * @param request Mono returning request function that is executed every time the resulting RequestQueue's next is called.
 * @param resultHandler Function that is passed to the resulting RequestQueue's subscribe call.
 * @param errorHandler MonoTypeOperatorFunction to where errors are piped to.
 * @param destroy$ Either an Observable which closes the queue upon first value emission, or an operator function which
 * can close the queue based on any condition/event (e.g. `takeUntilDestroyed()`).
 */
export function requestQueueIgnoreConcurrent<P, R>(
    request: (p?: P) => Mono<R>,
    resultHandler: (r: R) => void,
    errorHandler: MonoTypeOperatorFunction<R>,
    destroy$?: Observable<unknown> | MonoTypeOperatorFunction<unknown>,
): RequestQueue<P, R> {
    return new RequestQueue<P, R>('ignoreConcurrent', request, resultHandler, errorHandler, destroy$);
}

/**
 * Helper function for constructing a {@link RequestQueue} instance which executes the requests one at a time, in the order
 * they were invoked. A request invoked while a previous one is still in progress is executed only after the previous one has
 * completed (or failed).
 *
 * @param request Mono returning request function that is executed every time the resulting RequestQueue's next is called.
 * @param resultHandler Function that is passed to the resulting RequestQueue's subscribe call.
 * @param errorHandler MonoTypeOperatorFunction to where errors are piped to.
 * @param destroy$ Either an Observable which closes the queue upon first value emission, or an operator function which
 * can close the queue based on any condition/event (e.g. `takeUntilDestroyed()`).
 */
export function requestQueueSequential<P, R>(
    request: (p?: P) => Mono<R>,
    resultHandler: (r: R) => void,
    errorHandler: MonoTypeOperatorFunction<R>,
    destroy$?: Observable<unknown> | MonoTypeOperatorFunction<unknown>,
): RequestQueue<P, R> {
    return new RequestQueue<P, R>('sequential', request, resultHandler, errorHandler, destroy$);
}
